import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

// Actions
import { getCampaignPlan } from "../../../actions/campaign-plan/CampaignPlanActions";
import { getBasicCampaignDetails } from "../../../actions/campaign/CampaignBasicInfoActions";
import { getRoadStretchesByCityId } from "../../actions/campaign-planning/CampaignPlanningRoadStretchesActions";

// Utils
import { useGetTgInfo } from "../../../mavin/utils/hooks/HooksUtil";

// Components
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import MediaDetailsTable from "../components/MediaDetailsTable";
import MapView from "../components/map-view/MapView";
import CampaignSidebar from "../../../components/campaign-sidebar/CampaignSidebar";
import { continueToPlanning } from "../components/CampaignRedirect";
import { isBuyerUserSpecific } from "../../../components/hoc/MediaAgencySpecific";
import Spinner from "../../../components/spinner/Spinner";
import CityOverview from "../components/CityOverview";
import {
  CampaignDetails,
  ContinueEditing,
  SaveAsDraft,
} from "../components/HeaderNav";

// Page Components
function DraftCityViewHeader({ campaign, cityId, campaignPlan = {} }) {
  const history = useHistory();

  // for DOOH only campaign
  const { isDigital } = campaignPlan || {};

  const EnableContinueEditing = isBuyerUserSpecific(ContinueEditing);

  const pageTitle = <CampaignDetails campaign={campaign} />;
  const pageActions = (
    <div className="d-flex align-items-center">
      {EnableContinueEditing && (
        <EnableContinueEditing
          onClickAction={() =>
            continueToPlanning(campaign.id, cityId, history, isDigital)
          }
        />
      )}
      <SaveAsDraft />
    </div>
  );

  return <PageHeader title={pageTitle} actions={pageActions} shadow={true} />;
}

/**
 * Page
 */
export default function CampaignDraftPage() {
  const dispatch = useDispatch();
  const { campaignId, cityId } = useParams();

  // Get campaignPlan
  const campaignPlan = useSelector((state) => state.campaignPlan.campaignPlan);
  const { targetGroupId, isDigital } = campaignPlan || {};

  // Get campaign
  const campaign = useSelector((state) => state.c_campaignBasicInfo.campaign);

  // Campaign Media
  const campaignMedia = useSelector(
    (state) => state.campaignMedia.campaignMedia
  );

  // City road stretches
  const roadStretchMap = useSelector(
    (state) => state.planningRoadStretches.roadStretchDetailsMap
  );

  // Loadings
  const campaignPlanLoading = useSelector(
    (state) => state.campaignPlan.campaignPlanLoading
  );
  const basicCampaignDetailsLoading = useSelector(
    (state) => state.c_campaignBasicInfo.campaignLoading
  );
  const roadStretchesByCityIdLoading = useSelector(
    (state) => state.planningRoadStretches.loading
  );
  const tgInfoLoading = useSelector(
    (state) => state.orgTargetGroup.tgInfoLoading
  );

  const isPageLoading =
    campaignPlanLoading ||
    basicCampaignDetailsLoading ||
    roadStretchesByCityIdLoading ||
    tgInfoLoading;

  useEffect(() => {
    dispatch(getCampaignPlan(campaignId, cityId));
    dispatch(getBasicCampaignDetails(campaignId));
  }, [dispatch, campaignId, cityId]);

  // road stretches
  useEffect(() => {
    if (Object.keys(campaignPlan).length > 0) {
      dispatch(getRoadStretchesByCityId(cityId, "", isDigital));
    }
  }, [dispatch, JSON.stringify(campaignPlan)]);

  // tg info
  useGetTgInfo(targetGroupId);

  // Sidebar Settings
  const citySelected = { cityId };
  const redirectLinkObj = {
    campaignOverviewLink: `/campaign/${campaignId}/draft`,
    citySummaryLink: `/campaign/${campaignId}/city/:cityId/draft`,
  };

  // page loading
  if (isPageLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <>
      {/* SideBar */}
      <CampaignSidebar
        campaign={campaign}
        citySelected={citySelected}
        redirectLinkObj={redirectLinkObj}
      />

      <div className="content-wrapper">
        <DraftCityViewHeader
          campaign={campaign}
          cityId={cityId}
          campaignPlan={campaignPlan}
        />

        <div className="page-content">
          <CityOverview
            campaign={campaign}
            cityPlan={campaignPlan}
            campaignMedias={campaignMedia}
            roadStretchMap={roadStretchMap}
          />

          {/* Map View */}
          <MapView
            campaignPlan={campaignPlan}
            roadStretchMap={roadStretchMap}
          />

          {/* Media Sites Table */}
          <MediaDetailsTable
            campaignId={campaignId}
            campaign={campaign}
            campaignPlan={campaignPlan}
            roadStretchMap={roadStretchMap}
          />
        </div>
      </div>
    </>
  );
}
